/**
 * Time Zone Converter Tool
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

function parseDateInput(input: string, assumeUtc: boolean): Date {
  const trimmed = String(input ?? '').trim();
  if (!trimmed) throw ToolError.validation('Enter a date/time or a timestamp');

  // Timestamp seconds/ms
  if (/^[+-]?\d+$/.test(trimmed)) {
    let n = Number(trimmed);
    if (!Number.isFinite(n)) throw ToolError.validation('Invalid timestamp');
    if (Math.abs(n) < 1e11) n *= 1000;
    return new Date(n);
  }

  // If no timezone provided and assumeUtc is true, append Z
  const hasTz = /[zZ]|[+-]\d\d:?\d\d$/.test(trimmed);
  const normalized = assumeUtc && !hasTz ? `${trimmed}Z` : trimmed;
  const d = new Date(normalized);
  if (Number.isNaN(d.getTime())) throw ToolError.validation(`Invalid date: "${trimmed}"`);
  return d;
}

function formatInTimeZone(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}:${get('second')}`;
}

export const TimeZoneConverterTool: ITransformerTool = {
  id: 'time-zone-converter',
  name: 'Time Zone Converter',
  description: 'Format a date/time in a chosen IANA time zone',
  version: '1.0.0',
  type: 'transformer',
  category: 'Utilities',
  keywords: ['time', 'timezone', 'iana', 'convert', 'date'],

  supportedInputTypes: [SEMANTIC_TYPES.TEXT, SEMANTIC_TYPES.TIMESTAMP],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'timeZone',
      type: 'string',
      label: 'Time Zone',
      defaultValue: 'UTC',
      description: 'IANA time zone (e.g., UTC, America/New_York, Europe/London)',
    },
    {
      name: 'assumeUtc',
      type: 'boolean',
      label: 'Assume UTC',
      defaultValue: true,
      description: 'If input has no timezone, treat it as UTC',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const timeZone = String(config.timeZone ?? 'UTC').trim() || 'UTC';
    const assumeUtc = config.assumeUtc !== false;

    const date = parseDateInput(input, assumeUtc);

    try {
      const local = formatInTimeZone(date, timeZone);
      return `Time Zone: ${timeZone}\nLocal: ${local}\nUTC: ${date.toISOString()}`;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Invalid time zone';
      throw ToolError.validation(msg);
    }
  },
};
